import type { MediaAssetDescriptor } from "@quizshow/protocol";

export const imageLimitBytes = 5 * 1024 * 1024;
export const audioLimitBytes = 15 * 1024 * 1024;
export const roomMediaLimitBytes = 100 * 1024 * 1024;

const imageTypes = new Set(["image/jpeg", "image/png", "image/webp"]);
const audioTypes = new Set(["audio/mpeg", "audio/ogg", "audio/wav", "audio/x-wav"]);

async function optimizeImage(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 2200 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Bildoptimierung ist in diesem Browser nicht verfügbar.");
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const optimized = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/webp", 0.84));
  if (!optimized) throw new Error("Bild konnte nicht optimiert werden.");
  return optimized.size < file.size ? optimized : file;
}

export async function prepareMediaFile(
  file: File,
  kind: MediaAssetDescriptor["kind"],
): Promise<{ descriptor: MediaAssetDescriptor; blob: Blob }> {
  const isImage = kind === "image" || kind === "background" || kind === "icon";
  if (isImage && !imageTypes.has(file.type)) {
    throw new Error("Nur JPEG, PNG und WebP sind erlaubt. SVG- und HTML-Uploads sind gesperrt.");
  }
  if (!isImage && !audioTypes.has(file.type)) {
    throw new Error("Nur MP3, OGG und WAV sind erlaubt.");
  }
  const sourceLimit = isImage ? imageLimitBytes : audioLimitBytes;
  if (file.size > sourceLimit) {
    throw new Error(`${isImage ? "Bild" : "Audio"} überschreitet das Uploadlimit.`);
  }
  const blob = isImage ? await optimizeImage(file) : file;
  const mediaId = crypto.randomUUID();
  return {
    descriptor: {
      mediaId,
      kind,
      fileName: blob === file ? file.name : `${file.name.replace(/\.[^.]+$/, "")}.webp`,
      mimeType: blob.type || file.type,
      bytes: blob.size,
    },
    blob,
  };
}
